import { MILESTONES, SIM_TICK } from "./core/constants";
import { findNode, hasMilestone, regionExpansionCost, step } from "./core/sim";
import { applyReplayCommand } from "./core/replay";
import type { ReplayAction, ReplayCommand } from "./core/replay";
import type { GameState, SimEvent } from "./core/types";
import { createGame, wantedGoods, worldRect } from "./core/world";
import { MapRenderer } from "./render/canvas";
import { focusRegion } from "./render/view";
import { palette } from "./render/palette";
import { euro, need, pct } from "./ui/format";
import { Hud } from "./ui/hud";
import { bindInput } from "./ui/input";
import { Panel } from "./ui/panel";
import { createUiState, FLOAT_LIFETIME } from "./ui/state";
import { GOODS, WORLD_W } from "./core/constants";
import { activeSlotOf } from "./core/save";
import { bindSaveMenu } from "./ui/saves";
import { bindSheet } from "./ui/sheet";
import { createReplaySlot, newSeed, readSave, writeReplay, writeWorld } from "./ui/storage";

/** Größter Zeitschritt pro Frame. Verhindert Tunneln nach Tab-Wechsel. */
const MAX_DT = 0.05;

export function startGame(): void {
  const canvas = need<HTMLCanvasElement>("map");
  const renderer = new MapRenderer(canvas);
  const ui = createUiState();

  // Resume the active world, or open a slot for a fresh one. There is no state
  // in which the game runs without a world — the slot has to exist before the
  // first frame, because autosave no-ops while none is active.
  const resumed = activeSlotOf(readSave());
  const state: GameState = resumed?.world ?? createGame(newSeed());
  // Pinned once: every save below belongs to this world, and the save menu makes
  // another slot active before the page reloads.
  const slotId = resumed?.id ?? createReplaySlot(state);
  const commands: ReplayCommand[] = resumed?.kind === "replay" ? [...resumed.commands] : [];
  let tick = resumed?.kind === "replay" ? resumed.tick : -1;
  const persistGame = (): void => {
    if (resumed?.kind === "snapshot") writeWorld(state, slotId);
    else writeReplay(slotId, commands, tick, state);
  };
  const runCommand = (action: ReplayAction): boolean => {
    const command: ReplayCommand = { ...action, tick: tick + 1 };
    if (!applyReplayCommand(state, command)) return false;
    commands.push(command);
    return true;
  };

  const panel = new Panel(ui, runCommand);
  const hud = new Hud(ui);

  bindSheet();
  const openSaves = bindSaveMenu(persistGame, ui, hud);
  // A world nobody was asked to name is a world the player never learns the
  // menu exists for.
  if (!resumed) openSaves(slotId);

  bindInput({ canvas, renderer, getState: () => state, ui, panel, hud, runCommand });
  const regionControls = need<HTMLElement>("region-controls");
  const regionSlider = need<HTMLInputElement>("region-slider");
  const regionPosition = need<HTMLOutputElement>("region-position");
  const regionExpand = need<HTMLButtonElement>("region-expand");
  const focus = (index: number): void => {
    renderer.setWorldRect(worldRect(state));
    renderer.setView(focusRegion(renderer.getView(), index));
  };
  regionSlider.addEventListener("input", () => focus(Number(regionSlider.value) - 1));
  regionSlider.addEventListener("keydown", (event) => {
    if (event.key === "Home") focus(0);
    if (event.key === "End") focus(state.regionCount - 1);
  });
  regionExpand.addEventListener("click", () => {
    const before = state.regionCount;
    if (runCommand({ type: "expandRegion" })) focus(before);
    panel.invalidate();
  });
  const syncRegions = (): void => {
    const unlocked = hasMilestone(state, "veredelung");
    regionControls.hidden = !unlocked;
    if (!unlocked) return;
    regionSlider.max = String(state.regionCount);
    const view = renderer.getView();
    const current = view.cx < WORLD_W
      ? 1
      : Math.min(state.regionCount, Math.floor((view.cx - WORLD_W) / 600) + 2);
    regionSlider.value = String(current);
    regionPosition.value = `${current} / ${state.regionCount}`;
    const cost = regionExpansionCost(state);
    regionExpand.disabled = state.regionCount >= 12 || state.money < cost;
    regionExpand.textContent = state.regionCount >= 12 ? "Alle Regionen erschlossen" : `Region ${state.regionCount + 1} erschließen · ${euro(cost)}`;
  };

  // Gesichert wird beim Tageswechsel — dazwischen liegen bis zu DAY_LENGTH
  // Sekunden Echtzeit, die ein Absturz oder ein weggewischtes Tab kostet.
  // `pagehide` schließt das Fenster: anders als `beforeunload` feuert es auch,
  // wenn iOS die Seite in den Backforward-Cache legt und nie wieder aufweckt.
  // Es feuert dort auch beim bloßen Tabwechsel — ein paar Kilobyte in den
  // localStorage sind das wert.
  window.addEventListener("pagehide", persistGame);
  // The wrapper, not the window: the bottom sheet opening and closing resizes
  // the map without a window resize event, and the canvas would keep its old
  // backing store and be CSS-stretched — square grid cells stop being square.
  // A mobile URL bar retracting is the same failure with no event at all.
  const mapWrap = canvas.parentElement;
  if (!mapWrap) throw new Error("#map braucht einen Wrapper — .map-wrap fehlt im HTML");
  new ResizeObserver(() => renderer.resize()).observe(mapWrap);
  renderer.resize();

  function applyEvents(events: SimEvent[]): void {
    const p = palette();
    for (const ev of events) {
      if (ev.type === "delivered") {
        const node = findNode(state, ev.nodeId);
        ui.floats.push({
          x: node.x + (ev.units % 5) * 6 - 12,
          y: node.y - 38,
          text: `+${euro(ev.amount)}`,
          color: p.success,
          life: FLOAT_LIFETIME,
        });
      } else if (ev.type === "upkeep") {
        ui.floats.push({
          x: WORLD_W / 2,
          y: 46,
          text: `Unterhalt −${euro(ev.amount)}`,
          color: p.error,
          life: FLOAT_LIFETIME,
        });
      } else if (ev.type === "dayChanged") {
        persistGame();
      } else if (ev.type === "milestone") {
        hud.announce(ev.id);
        // A milestone changes what a card may offer — "verkehr" is what puts the
        // lane button on a route (RFC-0014). The panel only rebuilds on a
        // selection change, so without this the player who unlocked congestion
        // while a route was selected has to click away and back to see it.
        panel.invalidate();
      } else if (ev.type === "disruptionStarted" || ev.type === "disruptionEnded") {
        // Der Chip in der Kopfzeile sagt, was läuft; dieser Float sagt, dass sich
        // gerade etwas geändert hat — sonst müsste der Spieler die Kopfzeile
        // beobachten, um einen Preissprung zu bemerken.
        const fuel = ev.disruption.kind === "fuel";
        const name = fuel ? "Treibstoff" : "Ausgleich";
        const size = pct(Math.abs(ev.disruption.factor - 1));
        ui.floats.push({
          x: WORLD_W / 2,
          y: 78,
          text: ev.type === "disruptionStarted"
            ? `${name}: Unterhalt ${fuel ? "+" : "−"}${size}`
            : `${name} vorbei`,
          color: fuel ? p.error : p.success,
          life: FLOAT_LIFETIME,
        });
      } else if (ev.type === "throughputStarted") {
        // Am Ort, nicht in der Mitte: diese Störung hat eine Adresse, und der
        // Float ist die einzige Stelle, an der die Karte selbst sie zeigt.
        const label = { outage: "Ausfall", slump: "Einbruch", closure: "Sperrung" };
        const node = ev.nodeId === null ? null : findNode(state, ev.nodeId);
        ui.floats.push({
          x: node?.x ?? WORLD_W / 2,
          y: node?.y ?? 78,
          text: `${label[ev.disruption.kind]} −${pct(1 - ev.disruption.factor)}`,
          color: p.warning,
          life: FLOAT_LIFETIME,
        });
      } else if (ev.type === "demandShifted") {
        // At both addresses, and naming the new good: a shift is applied once
        // and then it simply *is* the world, so this float is the only notice
        // the player ever gets. A line in the header would need a lifetime the
        // HUD does not have — the chip up there belongs to weather that runs.
        for (const id of [ev.a, ev.b]) {
          const city = findNode(state, id);
          if (city.kind !== "city") continue;
          const wants = wantedGoods(city).map((g) => GOODS[g].name).join(" + ");
          ui.floats.push({
            x: city.x,
            y: city.y,
            text: `${city.name} will jetzt ${wants}`,
            color: p.warning,
            life: FLOAT_LIFETIME,
          });
        }
        // The card is per good since RFC-0016, so it is showing the appetite
        // that just changed. Same trap as the milestone above: without this the
        // player who had the city selected keeps reading yesterday's goods.
        panel.invalidate();
      } else if (ev.type === "producerClosed") {
        // At the works, and in the colour of a setback: a closure is applied
        // once and is then simply the world, so this float is the only notice
        // there is — the card and the empty store are what remain (RFC-0017
        // §3.5).
        const node = findNode(state, ev.nodeId);
        ui.floats.push({
          x: node.x,
          y: node.y,
          text: `${node.name} macht dicht`,
          color: p.error,
          life: FLOAT_LIFETIME,
        });
        // Same trap as the milestone and the swap: the producer card offers two
        // buttons that a ruin may not have, and the panel only rebuilds on a
        // selection change.
        panel.invalidate();
      } else if (ev.type === "throughputEnded") {
        const label = { outage: "Ausfall", slump: "Einbruch", closure: "Sperrung" };
        ui.floats.push({
          x: WORLD_W / 2,
          y: 78,
          text: `${label[ev.disruption.kind]} vorbei`,
          color: p.success,
          life: FLOAT_LIFETIME,
        });
      }
    }
  }

  let last = performance.now();
  let accumulated = 0;
  function frame(now: number): void {
    // Alles im try: eine Ausnahme aus step(), draw() oder sync() übersprang
    // sonst das requestAnimationFrame unten — und zwar dauerhaft. Das Spiel
    // stand auf dem letzten Bild, der Browser lief weiter. Ein kaputter Frame
    // darf einen Fehler ins Log schreiben, nicht die Schleife beenden.
    try {
      const dt = Math.min(MAX_DT, (now - last) / 1000);
      last = now;
      accumulated += dt * ui.speed;
      while (accumulated >= SIM_TICK) {
        const events = step(state, SIM_TICK);
        accumulated -= SIM_TICK;
        // Counted before the events are handled, because one of them is
        // `dayChanged`, and that one saves. With the increment afterwards the
        // slot stored a tick one short of the world it was written from, so the
        // reconstruction never matched and every autosave quietly turned the
        // replay back into a snapshot.
        tick++;
        applyEvents(events);
      }

      for (const f of ui.floats) {
        f.life -= dt;
        f.y -= 22 * dt;
      }
      ui.floats = ui.floats.filter((f) => f.life > 0);

      renderer.draw(state, ui);
      syncRegions();
      hud.sync(state);
      panel.sync(state);
    } catch (err) {
      console.error("Frame abgebrochen:", err);
    }

    requestAnimationFrame(frame);
  }

  requestAnimationFrame(frame);

  // Für Browsertests und schnelles Herumprobieren in der Konsole. `hud` liegt
  // mit drin, seit der Browsercheck eine Handbuchseite direkt öffnen muss, um zu
  // beweisen, dass eine nicht freigeschaltete es nicht tut (RFC-0013 §6).
  // `panel` kam mit RFC-0014 dazu: ein Test, der einen Meilenstein von Hand in
  // den State schreibt, umgeht das Ereignis, das die Karte sonst neu bauen lässt.
  // `MILESTONES` kam mit RFC-0023 dazu: der Browsercheck muss den State über eine
  // Schwelle schieben, und die Schwelle abzuschreiben macht ihn zu einer zweiten
  // Stelle, an der eine Balancing-Zahl steht. Genau daran ist er gefallen, als
  // die erste von 10 000 auf 11 500 € stieg.
  Object.defineProperty(window, "__umschlag", {
    value: { get state() { return state; }, ui, renderer, hud, panel, MILESTONES },
  });
}
